import { Component, ElementRef, EventEmitter, Inject, Output, signal, ViewChild, inject } from '@angular/core'
import { ScreenSizeService } from "../services/screen-size.service"
import { ElasticsearchService } from "./elasticsearch.service"
import { Subject } from "rxjs"
import { WagtailSearchResultItemInterface } from "./models"
import { Router } from "@angular/router"
import { ProductCollection } from "@gea-waldviertler-website/gea-models"
import { Product, ProductVariantInterface } from "@gea-waldviertler-website/gea-models"
import { EnvService } from "@gea-waldviertler-website/shop-lib"

@Component({
    selector: 'gea-shop-search-results',
    templateUrl: './search-results.component.html',
    standalone: false
})
export class SearchResultsComponent {
  private envService = inject(EnvService)

  productCollection = signal<ProductCollection | undefined>(undefined)
  keyboardNavigationIndex = signal<number>(-1)
  cmsSearchResults = signal<WagtailSearchResultItemInterface[] | undefined>(undefined)
  callSelectionIndex?: number

  private keyboardNavigationIndexSubject: Subject<number>
  private productCollectionSubject: Subject<ProductCollection | undefined>
  private cmsSearchResultsSubject: Subject<WagtailSearchResultItemInterface[] | undefined>
  private callSelectionIndexSubject: Subject<number>

  @Output("closeSearch") closeSearchEvent = new EventEmitter<boolean>()

  private itemsPerRow = {
    xs: 1, sm: 1, md: 2, lg: 3, xl: 4, xxl: 4
  }
  @ViewChild('searchResults') searchResults!: ElementRef<HTMLDivElement>

  constructor(
    @Inject(ScreenSizeService) private ssService: ScreenSizeService,
    @Inject(ElasticsearchService) private es: ElasticsearchService,
    private router: Router
  ) {
    this.keyboardNavigationIndexSubject = this.es.getKeyboardNavigationIndex()
    this.keyboardNavigationIndexSubject.subscribe(v => {
      this.keyboardNavigationIndex.set(v)
      if (v > -1) {
        this.scrollIntoView()
      }
    })

    this.productCollectionSubject = this.es.getProductCollection()
    this.productCollectionSubject.subscribe(c => {
      this.productCollection.set(c)
    })

    this.cmsSearchResultsSubject = this.es.getCmsSearchResults()
    this.cmsSearchResultsSubject.subscribe(r => {
      this.cmsSearchResults.set(r)
    })

    this.callSelectionIndexSubject = this.es.getCallSelectionEvent()
    this.callSelectionIndexSubject.subscribe(value => {
      this.callSelectionIndex = value
      // const value: undefined | number = changes['callSelectionIndex'].currentValue
      if (value === undefined || value === null) return
      if (value > -1) {
        const el = this.getActiveAnchorElement()
        if (el) el.click()
      }
    })
  }

  getActiveAnchorElement(): HTMLAnchorElement | null {
    return this.getActiveNavigationElement()!.querySelector('a')
  }

  getActiveNavigationElement(): HTMLDivElement | null {
    let el: HTMLDivElement | null = null
    const index = this.keyboardNavigationIndex()
    if (index == 0) {
      el = this.searchResults.nativeElement.querySelector('.item:first-child') as HTMLDivElement
    } else {
      el = this.searchResults.nativeElement.querySelector('.item:nth-child(' + (index + 1) + ')') as HTMLDivElement
    }

    return el
  }

  scrollIntoView() {
    const itemsPerRow = this.itemsPerRow[this.ssService.getCurrentDimension()]
    const index = this.keyboardNavigationIndex()
    if (index < itemsPerRow) {
      this.searchResults.nativeElement.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const elInRow = Math.ceil(index / itemsPerRow)
    const el = this.getActiveNavigationElement()

    if (el) {
      const topPos = elInRow * el.offsetHeight
      this.searchResults.nativeElement.scrollTo({ top: topPos, behavior: 'smooth' })
    }
  }

  onClickCmsItem(item: WagtailSearchResultItemInterface) {
    window.location.href = item.url
  }

  onClickProduct(e: MouseEvent, product: Product<ProductVariantInterface>) {
    // from click on swatch color
    if (e.defaultPrevented) {
      this.onAfterClicked()
    } else {
      this.router.navigateByUrl(product.slug + '.html')
      this.onAfterClicked()
    }
  }

  onAfterClicked() {
    this.closeSearchEvent.emit(true)
    this.es.getToggleSearchSubject().next(false)
    this.productCollection.set(undefined)
    this.productCollectionSubject.next(undefined)
    this.cmsSearchResults.set(undefined)
    this.cmsSearchResultsSubject.next(undefined)
    this.keyboardNavigationIndexSubject.next(-1)
    let that = this
    setTimeout(() => {
      that.closeSearchEvent.emit(false)
    }, 200)

  }

  resolveCmsMediaUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }
    const baseUrl = this.envService?.config?.cmsMediaUrl?.replace(/\/+$/, '') || ''
    const cleanPath = path.replace(/^\/media/, '').replace(/^\/+/, '')
    return `${baseUrl}/${cleanPath}`
  }

  getCmsImageSrc(item: WagtailSearchResultItemInterface): string | undefined {
    if (!item.image) return undefined

    let chosenPath: string | undefined
    if (item.image.renditions) {
      const renditions = Object.values(item.image.renditions)
      const goodWebp = renditions.find(r => Number(r.width) >= 150 && r.type === 'image/webp')
      if (goodWebp) {
        chosenPath = goodWebp.path
      } else {
        const anyWebp = renditions.find(r => r.type === 'image/webp')
        if (anyWebp) {
          chosenPath = anyWebp.path
        } else if (renditions.length > 0) {
          chosenPath = renditions[0].path
        }
      }
    }

    if (!chosenPath && item.image.path) {
      chosenPath = item.image.path
    }

    if (!chosenPath) return undefined

    return this.resolveCmsMediaUrl(chosenPath)
  }
}
